import React, { useState, useEffect } from 'react';
/* COMPONENTS */
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Grid,
    TextField,
    InputAdornment,
    Autocomplete,
    Box,
    autocompleteClasses,
    Switch,
    FormControlLabel,
} from '@mui/material';
/* LAB */
import { LoadingButton } from '@mui/lab';
/* TRANSLATE */
import { useTranslation } from 'react-i18next';
/* ICONS */
import CloseIcon from '@mui/icons-material/Close';
import { Visibility } from '@mui/icons-material';
import { VisibilityOff } from '@mui/icons-material';
/* REQUEST */
import { getUserById, getProfiles, editUser } from 'utils/Users';
import { validatePassword, handleHash, validateEmail } from 'utils/Login';

const EditUser = ({ open, setOpen, setAlert, setOpenAlert, setContextMenu, reloading, selected }) => {
    const { t } = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [checked, setChecked] = useState(false);
    const [profiles, setProfiles] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorEmail, setErrorEmail] = useState(false);
    const [helperText, setHelperText] = useState('');
    const [helperTextPassword, setHelperTextPassword] = useState('');
    const [helperTextEmail, setHelperTextEmail] = useState('');
    const [errorPassword, setErrorPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    useEffect(() => {
        const response = async () => {
            try {
                setLoading(true);

                const [user, profile] = await Promise.allSettled([getUserById(setLoading, selected[0]), getProfiles(setLoading)]);

                const userData = user.status === 'fulfilled' ? user.value?.data : null;
                const profilesData = profile.status === 'fulfilled' ? profile.value?.data : null;

                if (userData) {
                    setUsername(userData.username);
                    setEmail(userData.email);

                    // Asignar el perfil basado en el profile_id del usuario
                    if (userData.profile?.profile_id && profilesData) {
                        // Buscar el perfil completo que coincida con el profile_id del usuario
                        const userProfile = profilesData.find((p) => p.profile_id === userData.profile.profile_id);
                        if (userProfile) {
                            setSelectedProfile(userProfile);
                        }
                    }
                } else {
                    console.error('Error fetching user:', user.reason?.message);
                }

                if (profilesData) {
                    setProfiles(profilesData);
                } else {
                    console.error('Error fetching profiles:', profile.reason?.message);
                }
            } catch (error) {
                console.error('Error in data fetching:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && selected.length > 0) {
            response();
        }
    }, [open, selected]);

    const matchPassword = (password, passwordConfirm) => {
        const match = password === passwordConfirm;
        if (!match) {
            setErrorPassword(true);
            setHelperTextPassword(t('user_management.table.dialog.edit.password_not_match'));
        } else {
            setErrorPassword(false);
            setHelperTextPassword('');
        }
    };

    const cleanForm = () => {
        setOpen(false);
        setUsername('');
        setPassword('');
        setSelectedProfile('');
        setProfiles(null);
        setEmail('');
        setLoading(false);
        setError(false);
        setHelperText('');
        setErrorPassword(false);
        setHelperTextPassword('');
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setPasswordConfirm('');
        setChecked(false);
        setErrorEmail(false);
        setHelperTextEmail('');
    };

    const cleanAll = () => {
        setOpen(false);
        setUsername('');
        setPassword('');
        setSelectedProfile('');
        setProfiles(null);
        setEmail('');
        setAlert({ message: '', severity: '' });
        setLoading(false);
        setError(false);
        setHelperText('');
        setErrorPassword(false);
        setHelperTextPassword('');
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setPasswordConfirm('');
        setChecked(false);
        setErrorEmail(false);
        setHelperTextEmail('');
        setContextMenu(false);
    };

    const cleanPassword = () => {
        setPassword('');
        setPasswordConfirm('');
        setErrorPassword(false);
        setHelperTextPassword('');
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setHelperText('');
        setError(false);
    };

    return (
        <Dialog open={open} onClose={() => cleanAll()} fullWidth maxWidth="xs">
            <DialogTitle>{t('user_management.table.dialog.edit.title')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => cleanAll()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            sx={{ mt: 1 }}
                            id={'input-username'}
                            fullWidth
                            label={t('user_management.table.dialog.add.username')}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            autoComplete="off"
                            required
                            disabled={loading}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={checked}
                                    onChange={(e) => {
                                        setChecked(e.target.checked);
                                        cleanPassword();
                                    }}
                                    name="checked"
                                    color="primary"
                                />
                            }
                            label={t('user_management.table.dialog.edit.change_password')}
                            disabled={loading}
                        />
                    </Grid>
                    {checked && (
                        <>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <TextField
                                    sx={{ mt: 1 }}
                                    id={'input-password'}
                                    onBlur={(e) => (e.target.value !== '' ? validatePassword(e.target.value, setError, setHelperText) : null)}
                                    fullWidth
                                    error={error}
                                    helperText={helperText}
                                    type={showPassword ? 'text' : 'password'}
                                    label={t('user_management.table.dialog.add.password')}
                                    value={password}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setPassword(e.target.value);
                                            setError(false);
                                            setHelperText('');
                                        } else {
                                            const newValue = e.target.value;
                                            setPassword(newValue);

                                            // Puedes validar mientras escribe o al terminar de escribir usando onBlur
                                            validatePassword(newValue, setError, setHelperText);
                                        }
                                    }}
                                    required
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ backgroundColor: 'transparent' }}>
                                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <TextField
                                    sx={{ mt: 1 }}
                                    id={'input-password-confirm'}
                                    onBlur={(e) => (e.target.value !== '' ? matchPassword(e.target.value, password) : null)}
                                    fullWidth
                                    error={errorPassword}
                                    helperText={helperTextPassword}
                                    type={showPasswordConfirm ? 'text' : 'password'}
                                    label={t('user_management.table.dialog.edit.password_confirmation')}
                                    value={passwordConfirm}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setPasswordConfirm(e.target.value);
                                            setErrorPassword(false);
                                            setHelperTextPassword('');
                                        } else {
                                            const newValue = e.target.value;
                                            setPasswordConfirm(newValue);

                                            // Puedes validar mientras escribe o al terminar de escribir usando onBlur
                                            matchPassword(newValue, password);
                                        }
                                    }}
                                    required
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ backgroundColor: 'transparent' }}>
                                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                                                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            sx={{ mt: 1 }}
                            id={'input-email'}
                            fullWidth
                            onBlur={(e) => (e.target.value !== '' ? validateEmail(e.target.value) : null)}
                            label={t('user_management.table.dialog.edit.email')}
                            value={email}
                            required
                            helperText={helperTextEmail}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (validateEmail(e.target.value)) {
                                    setErrorEmail(false);
                                    setHelperTextEmail('');
                                } else {
                                    setErrorEmail(true);
                                    setHelperTextEmail(t('user_management.table.validataion.invalid_email'));
                                }
                            }}
                            autoComplete="off"
                            error={errorEmail}
                            disabled={loading}
                        ></TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Autocomplete
                            disabled={loading}
                            value={selectedProfile ? selectedProfile : null}
                            options={profiles ? profiles.sort((a, b) => -b.name.localeCompare(a.name)) : []}
                            isOptionEqualToValue={(option, value) => option.profile_id === value.profile_id}
                            getOptionLabel={(option) => option.name}
                            onChange={(e, newValue) => setSelectedProfile(newValue)}
                            sx={{ width: '100%', mt: 1 }}
                            renderInput={(params) => <TextField {...params} required label={t('user_management.table.dialog.add.select.select_profile')} />}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <Box
                                        key={key}
                                        sx={{
                                            borderRadius: '5px',
                                            margin: '5px',
                                            [`&.${autocompleteClasses.option}`]: {
                                                padding: '8px',
                                            },
                                        }}
                                        component="li"
                                        {...optionProps}
                                    >
                                        {option.name}
                                    </Box>
                                );
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        height: 200,
                                        overflowY: 'auto',
                                    },
                                },
                                popper: {
                                    placement: 'bottom-start',
                                    modifiers: [{ name: 'flip', enabled: false }],
                                },
                                textField: () => ({
                                    required: true,
                                }),
                            }}
                            required
                            noOptionsText={t('user_management.table.dialog.add.select.no_options')}
                            loadingText={t('user_management.table.dialog.add.select.loading')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => cleanForm()} color="primary">
                    {t('user_management.table.dialog.edit.buttons.cancel')}
                </Button>
                <LoadingButton
                    loading={loading}
                    variant="contained"
                    disableElevation
                    onClick={async () => {
                        try {
                            const edit = {
                                profile_id: selectedProfile.profile_id,
                                username: username,
                                email: email,
                                password: password ? await handleHash(password) : null,
                            };
                            await editUser(setLoading, edit, selected[0]);

                            setAlert({ msg: t('user_management.table.dialog.edit.messages.success'), severity: 'success' });
                            setOpenAlert(true);
                            cleanForm();
                            reloading();
                        } catch (error) {
                            console.error('Error editing user:', error.message);
                            setAlert({ msg: t('user_management.table.dialog.edit.messages.error'), severity: 'error' });
                            setOpenAlert(true);
                            setLoading(false);
                            setContextMenu(null);
                        }
                    }}
                    disabled={username === '' || email === '' || selectedProfile === null || passwordConfirm === ''}
                    color="primary"
                >
                    {t('user_management.table.dialog.edit.buttons.edit')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export { EditUser };
